export const COEXISTENCE_STATES = ['OFFBOARDED', 'DISCONNECTED'] as const;

export type CoexistenceState = (typeof COEXISTENCE_STATES)[number];

export interface CoexistenceNotice {
  state: CoexistenceState;
  tone: 'info' | 'error';
  headline: string;
  guidance: string;
  cause: string | null;
  reconnect: { label: string; to: string } | null;
}

export const COEXISTENCE_RECONNECT_PATH = '/home/integrations/whatsapp-business';

export const COEXISTENCE_REASON_COPY: Record<string, string> = {
  PRIMARY_INACTIVITY:
    'the WhatsApp Business app on the main phone had not been opened for about two weeks',
  COMPANION_INACTIVITY:
    'this number had been inactive on its linked devices for about a month',
  USER_RE_REGISTERED: 'WhatsApp was re-registered on a new device',
  CHANGE_NUMBER: 'the WhatsApp account was moved to a different phone number',
  // Meta: "disconnected due to enforcement or because your client explicitly
  // deleted their WhatsApp account".
  ACCOUNT_DISCONNECTED:
    'the WhatsApp account was deleted, or blocked by Meta',
  BUSINESS_DOWNGRADE:
    'the WhatsApp Business account was changed back to a personal account',
  // Meta account_update events stored when Meta gives no finer reason.
  // Meta sends this both for an unlink in the WhatsApp Business app and for a
  // removal in Business Settings, so the copy names both.
  PARTNER_REMOVED:
    'WeNext was disconnected from this WhatsApp account (in the WhatsApp Business app or Meta Business Settings)',
  ACCOUNT_DELETED: 'the WhatsApp Business account was deleted in Meta',
  PARTNER_APP_UNINSTALLED:
    'the WeNext app was removed from your Meta business account',
  // Found from a failed Meta call and confirmed by a re-check (backend
  // whatsappConnectionLoss.js — keep both lists in step).
  ACCESS_REMOVED: 'WeNext lost access to this WhatsApp account in Meta',
  PASSWORD_CHANGED: 'the Facebook password used to connect was changed',
  APP_REMOVED:
    'the WeNext app was removed from the Facebook account used to connect',
  TOKEN_EXPIRED: "WeNext's access to Meta expired",
  TOKEN_INVALID: "Meta ended WeNext's access for security reasons",
  PERMISSION_REMOVED:
    "WeNext's permission for this WhatsApp account was removed",
  // The number itself (confirmed from Meta's phone-number status).
  NUMBER_DELETED:
    'this phone number was removed from your WhatsApp Business account',
  NUMBER_NOT_REGISTERED:
    'this phone number is no longer registered on the WhatsApp Business Platform',
  NUMBER_MIGRATED:
    'this phone number was moved to another WhatsApp Business account or provider',
};

export function normalizeCoexistenceState(
  value: string | null | undefined
): CoexistenceState | null {
  return (COEXISTENCE_STATES as readonly string[]).includes(value as string)
    ? (value as CoexistenceState)
    : null;
}

export function isCoexistenceDegraded(
  value: string | null | undefined
): boolean {
  return normalizeCoexistenceState(value) !== null;
}

export function describeCoexistenceReason(
  reason: string | null | undefined
): string | null {
  if (typeof reason !== 'string') return null;
  const key = reason.trim().toUpperCase();
  if (!key) return null;
  return COEXISTENCE_REASON_COPY[key] ?? null;
}

function accountLabel(
  accountName?: string | null,
  phoneNumber?: string | null
): string {
  if (accountName && phoneNumber) return `${accountName} (${phoneNumber})`;
  return accountName || phoneNumber || 'Your WhatsApp number';
}

export interface BuildCoexistenceNoticeInput {
  state?: string | null;
  reason?: string | null;
  accountName?: string | null;
  phoneNumber?: string | null;
}

export function buildCoexistenceNotice({
  state,
  reason,
  accountName,
  phoneNumber,
}: BuildCoexistenceNoticeInput): CoexistenceNotice | null {
  const normalized = normalizeCoexistenceState(state);
  if (!normalized) return null;

  const label = accountLabel(accountName, phoneNumber);
  const cause = describeCoexistenceReason(reason);

  if (normalized === 'OFFBOARDED') {
    return {
      state: normalized,
      tone: 'info',
      headline: `${label} is restoring its WhatsApp connection.`,
      guidance:
        "Restoring automatically, usually within minutes. Finish re-registering WhatsApp on your phone and keep the WhatsApp Business app open — and don't link this number to another provider, or the restore is cancelled.",
      cause,
      reconnect: null,
    };
  }

  // Say WHAT is paused (all WhatsApp messages, campaigns and automations on
  // this number) and WHY, so the reader doesn't have to guess.
  return {
    state: normalized,
    tone: 'error',
    headline: `${label} has lost its connection to WeNext.`,
    guidance: cause
      ? `Sending is paused for all WhatsApp messages, campaigns and automations on this number because ${cause}. Click Reconnect to start again.`
      : 'Sending is paused for all WhatsApp messages, campaigns and automations on this number. Meta ended its connection to WeNext — click Reconnect to start again.',
    cause,
    reconnect: { label: 'Reconnect', to: COEXISTENCE_RECONNECT_PATH },
  };
}
